/**
 * The sheet document model (CLAUDE.md 21.2, ADR-0007, docs/SHEETS.md 3).
 *
 * A sheet is a *document*, never a program. The server stores this structure and
 * nothing else -- no executable code, no serialised closures -- which is what makes
 * "no user-authored content executes in our Python process" (21.1.1) cheap to
 * guarantee rather than a permanent arms race.
 *
 * Five cell types in v1. A sixth, `code`, is deliberately reserved in the union
 * comment and NOT built: adding it converts sheets from documents into programs and
 * needs its own ADR (ADR-0007).
 */

/** Stable within a sheet version. Error messages and comments cite the cell index,
 *  but everything internal refers to a cell by id, so reordering is cheap. */
export type CellId = string

export type CellType = 'math' | 'text' | 'table' | 'plot' | 'image'

/**
 * A canonicalised identifier. Base is a single Latin letter or a Greek name; the
 * subscript is opaque text. This is ordinary mathematical typography -- `W_pl,y`,
 * not `Wply` -- and it is what lets the parser treat `Ed` in `w_Ed` as one
 * subscript rather than two multiplied variables.
 */
export interface MathSymbol {
  readonly base: string
  readonly sub: string
}

/** Canonical key for scope lookups: `L`, `w_Ed`, `gamma_M0`, `W_pl,y`. */
export function symbolKey(sym: MathSymbol): string {
  return sym.sub ? `${sym.base}_${sym.sub}` : sym.base
}

export function parseSymbolKey(key: string): MathSymbol {
  const cut = key.indexOf('_')
  if (cut < 0) return { base: key, sub: '' }
  return { base: key.slice(0, cut), sub: key.slice(cut + 1) }
}

export interface MathCell {
  readonly id: CellId
  readonly type: 'math'
  /** LaTeX as MathLive emits it. The single source of truth for a math cell. */
  readonly latex: string
}

export interface TextCell {
  readonly id: CellId
  readonly type: 'text'
  /** Sanitised on write and again on render (21.1.4). Never raw author HTML. */
  readonly html: string
}

export interface TableRow {
  /** Canonical symbol key this row assigns, or '' for a label-only row. */
  readonly symbol: string
  readonly value: number | null
  /** Pint-parseable unit string, '' for dimensionless. */
  readonly unit: string
}

export interface TableCell {
  readonly id: CellId
  readonly type: 'table'
  readonly caption: string
  readonly rows: readonly TableRow[]
}

export interface PlotCell {
  readonly id: CellId
  readonly type: 'plot'
  readonly caption: string
  /** LaTeX for the quantity plotted on y. */
  readonly latex: string
  /** Canonical key of the swept symbol. */
  readonly sweep: string
  readonly from: number
  readonly to: number
  readonly steps: number
}

export interface ImageCell {
  readonly id: CellId
  readonly type: 'image'
  /**
   * The server-side asset id, or `null` until an image has been uploaded. The
   * document stores an *id*, never a URL: the bytes are fetched through the
   * authenticated, sandboxed file endpoint, and a URL in the document would be one
   * the author could point anywhere (docs/SHEETS.md 4.4).
   */
  readonly assetId: number | null
  readonly alt: string
  /** Display width in CSS pixels. Absent means the image's own width, capped by the column. */
  readonly widthPx?: number
}

export type Cell = MathCell | TextCell | TableCell | PlotCell | ImageCell

export interface SheetDoc {
  readonly id: string
  readonly title: string
  readonly cells: readonly Cell[]
  /** Bumped when a change to the engine could move a number (5.7 applied to sheets). */
  readonly engineVersion: string
}

/**
 * Evaluation state, per docs/SHEETS.md 3. Rendered distinctly, and never by colour
 * alone -- every state carries an icon and a word (14.2).
 */
export type CellStatus = 'ok' | 'evaluating' | 'stale' | 'unevaluated' | 'error'

export type EvalErrorKind =
  | 'units'
  /** Structurally unreachable inside one sheet under document-order scoping; retained
   *  because cross-sheet references (ADR-0010) can produce one. See graph.ts. */
  | 'cycle'
  | 'undefined'
  | 'syntax'
  | 'timeout'
  | 'limit'
  | 'internal'

export interface EvalError {
  readonly kind: EvalErrorKind
  /** i18n key. Never literal prose -- backend and frontend both localise (14.4). */
  readonly messageKey: string
  readonly params?: Readonly<Record<string, string | number>>
  /** Cells worth pointing at from the diagnostic, e.g. where a symbol was defined. */
  readonly relatedCells?: readonly CellId[]
}

/** A number with a unit, as the worker returns it. */
export interface Quantity {
  readonly value: number
  readonly unit: string
}

/** A matrix result. Every element shares the one unit (see engine/units.ts). */
export interface MatrixQuantity {
  readonly rows: readonly (readonly number[])[]
  readonly unit: string
}

export type ResultValue = Quantity | MatrixQuantity

export function isMatrixQuantity(value: ResultValue): value is MatrixQuantity {
  return 'rows' in value
}

export interface CellResult {
  readonly cellId: CellId
  readonly status: CellStatus
  /** Canonical keys this cell defines. A math cell defines at most one; a table
   *  defines one per row that names a symbol. */
  readonly defines: readonly string[]
  readonly value?: Quantity
  /** Present instead of `value` when the cell evaluates to a matrix. */
  readonly matrix?: MatrixQuantity
  /** Sampled series for a plot cell. */
  readonly series?: readonly (readonly [number, number])[]
  readonly error?: EvalError
}
